/**
 * Decals.js — deferred-style projected decals.
 * owner: fx
 *
 * These are NOT camera-facing quads. Each decal is an oriented box; the shader
 * reconstructs the world position of whatever geometry is behind each pixel from
 * the FX depth+normal buffer, transforms it into the decal's local space and
 * discards anything outside the unit cube. The result wraps around corners,
 * follows curvature and never floats off the surface.
 *
 * Shading is a *relighting ratio*: the crater's perturbed normal is lit with the
 * same sun+ambient the world uses and divided by the flat surface's response, so
 * the bullet hole reads as real geometry against whatever material is underneath
 * — a lit crater with a darkened rim, not a black circle.
 *
 * One instanced draw call for the whole buffer. 512 slots, recycled oldest-first
 * with a forced fade so nothing ever pops out.
 */
import * as THREE from 'three';
import { DEPTH_HELPERS } from './Shaders.js';

const DECAL_VS = /* glsl */`
precision highp float;
attribute vec4 iPos;      // xyz = centre, w = unused
attribute vec4 iQuat;
attribute vec4 iScale;    // xyz = half extents, w = tile index
attribute vec4 iParams;   // fade, normalStrength, tintStrength, edgeSoft
attribute vec4 iTint;

varying vec4 vQuat;
varying vec3 vCentre;
varying vec3 vHalf;
varying vec4 vParams;
varying vec4 vTint;
varying float vTile;

vec3 qrot(vec4 q, vec3 v){ return v + 2.0 * cross(q.xyz, cross(q.xyz, v) + q.w * v); }

void main(){
  if (iParams.x <= 0.0){ gl_Position = vec4(0.0, 0.0, -3.0, 1.0); return; }
  vec3 local = position * iScale.xyz * 2.0;
  vec3 world = iPos.xyz + qrot(iQuat, local);
  gl_Position = projectionMatrix * viewMatrix * vec4(world, 1.0);
  vQuat = iQuat; vCentre = iPos.xyz; vHalf = iScale.xyz;
  vParams = iParams; vTint = iTint; vTile = iScale.w;
}
`;

const DECAL_FS = /* glsl */`
precision highp float;
uniform sampler2D tAtlas;
uniform vec2 uResolution;
uniform mat4 uProjInv, uViewInv;
uniform mat3 uViewInvRot;
uniform vec3 uSunDirWorld, uSunColor, uAmbSky, uAmbGround;
uniform float uAtlasTexel;
uniform float uDebug;
${DEPTH_HELPERS}

varying vec4 vQuat;
varying vec3 vCentre;
varying vec3 vHalf;
varying vec4 vParams;
varying vec4 vTint;
varying float vTile;

vec3 qrot(vec4 q, vec3 v){ return v + 2.0 * cross(q.xyz, cross(q.xyz, v) + q.w * v); }
vec3 qrotInv(vec4 q, vec3 v){ return qrot(vec4(-q.xyz, q.w), v); }

vec2 tileUV(vec2 uv){
  vec2 t = vec2(mod(vTile, 4.0), floor(vTile / 4.0));
  return (t + clamp(uv, 0.0, 1.0)) * 0.25;
}

vec3 ambientAt(vec3 n){
  return mix(uAmbGround, uAmbSky, n.y * 0.5 + 0.5);
}

void main(){
  vec2 suv = gl_FragCoord.xy / uResolution;
  float sceneZ = fxSceneZ(suv);
  if (sceneZ >= 1.0e5) discard;                      // sky / nothing behind

  // reconstruct the world position of the surface under this pixel
  vec3 ndc = vec3(suv * 2.0 - 1.0, 1.0);
  vec4 vray = uProjInv * vec4(ndc, 1.0);
  vec3 dir = vray.xyz / vray.w;
  vec3 viewPos = dir * (sceneZ / max(1e-4, -dir.z));
  vec3 worldPos = (uViewInv * vec4(viewPos, 1.0)).xyz;
  vec3 worldN = normalize(uViewInvRot * fxSceneN(suv));

  vec3 local = qrotInv(vQuat, worldPos - vCentre) / vHalf;
  vec3 a = abs(local);
  if (a.x > 1.0 || a.y > 1.0 || a.z > 1.0) discard;

  vec3 axis = qrot(vQuat, vec3(0.0, 0.0, 1.0));
  float facing = dot(worldN, axis);
  float angFade = smoothstep(0.18, 0.62, facing);
  if (angFade <= 0.001) discard;

  float zFade = 1.0 - smoothstep(0.55, 1.0, a.z);
  float edge = (1.0 - smoothstep(0.86, 1.0, a.x)) * (1.0 - smoothstep(0.86, 1.0, a.y));

  vec2 duv = local.xy * 0.5 + 0.5;
  vec2 auv = tileUV(duv);
  vec4 s = texture2D(tAtlas, auv);

  float cov = s.a * vParams.x * angFade * zFade * edge;
  if (cov <= 0.002) discard;

  // crater normal from the height field, expressed in the decal's tangent frame
  float hx = texture2D(tAtlas, auv + vec2(uAtlasTexel, 0.0)).r - texture2D(tAtlas, auv - vec2(uAtlasTexel, 0.0)).r;
  float hy = texture2D(tAtlas, auv + vec2(0.0, uAtlasTexel)).r - texture2D(tAtlas, auv - vec2(0.0, uAtlasTexel)).r;
  vec3 tangent   = qrot(vQuat, vec3(1.0, 0.0, 0.0));
  vec3 bitangent = qrot(vQuat, vec3(0.0, 1.0, 0.0));
  float ns = vParams.y * 40.0;
  vec3 pn = normalize(worldN - tangent * hx * ns - bitangent * hy * ns);

  // relighting ratio — matches whatever the surface material already resolved to
  float ndl0 = max(dot(worldN, uSunDirWorld), 0.0);
  float ndl1 = max(dot(pn, uSunDirWorld), 0.0);
  vec3 base = uAmbSky * 0.0 + ambientAt(worldN) + uSunColor * ndl0;
  vec3 pert = ambientAt(pn) + uSunColor * ndl1;
  vec3 ratio = clamp(pert / max(base, vec3(1e-3)), vec3(0.25), vec3(2.6));

  // The bore and its chipped lip carry all the information; the broad dust ring
  // is atmosphere. Squaring the ring mask pulls it in tight around the hole, so
  // forty rounds into a wall stack forty small marks instead of forty
  // full-footprint washes that integrate into a flat sheet. Together with the
  // spatial density cap in add(), this is what stops combat repainting the
  // ground a uniform grey.
  float halo = s.b * s.b;
  vec3 mul = vec3(1.0);
  mul *= mix(vec3(1.0), ratio, cov);
  mul *= mix(vec3(1.0), vec3(s.g * 2.0), cov);   // 0.5 in the atlas == unchanged
  mul *= mix(vec3(1.0), vTint.rgb, clamp(halo * cov * vParams.z, 0.0, 1.0));
  // a bullet hole is a dark crater, never a black hole, and a stack of
  // overlapping decals must never be able to multiply a region to zero — nor
  // bleach it, which is the failure mode when the tint is above 1
  mul = clamp(mul, vec3(0.16), vec3(1.85));

  if (uDebug > 0.5){
    gl_FragColor = vec4(duv, cov, 1.0);
    return;
  }
  gl_FragColor = vec4(mul, 1.0);
}
`;

export class DecalSystem {
  constructor(res, opts = {}) {
    this.capacity = opts.capacity ?? 512;
    this.res = res;
    this.head = 0;
    this.count = 0;
    this.time = 0;

    /**
     * Spatial density cap.
     *
     * A firefight puts hundreds of rounds into a few square metres. Unbounded,
     * every one of them lays down a projected box that modulates the surface
     * under it, and a few dozen overlapping boxes stop reading as bullet holes
     * and start reading as a uniform wash over the ground — exactly what the AI
     * team saw in the `ai_*` shots. Shipped games cap decals per unit area, keep
     * the newest and retire the oldest in that patch; so do we.
     *
     * One hash-grid lookup per decal, no allocation in the steady state. Stale
     * entries are self-correcting: a slot that has been recycled elsewhere no
     * longer hashes into this cell and is dropped on the next visit.
     */
    this.cellSize = opts.cellSize ?? 0.42;
    this.maxPerCell = opts.maxPerCell ?? 2;
    this.minSeparation = opts.minSeparation ?? 0.075;
    this.grid = new Map();
    this.suppressed = 0;

    const cap = this.capacity;
    this.iPos = new Float32Array(cap * 4);
    this.iQuat = new Float32Array(cap * 4);
    this.iScale = new Float32Array(cap * 4);
    this.iParams = new Float32Array(cap * 4);
    this.iTint = new Float32Array(cap * 4);
    this.birth = new Float32Array(cap);
    this.life = new Float32Array(cap);
    this.fadeOut = new Float32Array(cap);
    this.used = new Uint8Array(cap);
    this.order = [];                      // slot indices, oldest first

    const g = new THREE.InstancedBufferGeometry();
    const box = new THREE.BoxGeometry(1, 1, 1);
    g.setAttribute('position', box.getAttribute('position'));
    g.setIndex(box.getIndex());
    box.dispose();
    this.aPos = new THREE.InstancedBufferAttribute(this.iPos, 4).setUsage(THREE.DynamicDrawUsage);
    this.aQuat = new THREE.InstancedBufferAttribute(this.iQuat, 4).setUsage(THREE.DynamicDrawUsage);
    this.aScale = new THREE.InstancedBufferAttribute(this.iScale, 4).setUsage(THREE.DynamicDrawUsage);
    this.aParams = new THREE.InstancedBufferAttribute(this.iParams, 4).setUsage(THREE.DynamicDrawUsage);
    this.aTint = new THREE.InstancedBufferAttribute(this.iTint, 4).setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('iPos', this.aPos);
    g.setAttribute('iQuat', this.aQuat);
    g.setAttribute('iScale', this.aScale);
    g.setAttribute('iParams', this.aParams);
    g.setAttribute('iTint', this.aTint);
    g.instanceCount = 0;
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);

    this.uniforms = {
      tDepth: { value: null }, tNormal: { value: null }, uDepthMode: { value: 0 },
      tAtlas: { value: res.textures.decals },
      uResolution: { value: new THREE.Vector2(1, 1) },
      uProjInv: { value: new THREE.Matrix4() },
      uViewInv: { value: new THREE.Matrix4() },
      uViewInvRot: { value: new THREE.Matrix3() },
      uSunDirWorld: { value: new THREE.Vector3(0, 1, 0) },
      uSunColor: { value: new THREE.Color(1, 1, 1) },
      uAmbSky: { value: new THREE.Color(0.2, 0.25, 0.3) },
      uAmbGround: { value: new THREE.Color(0.1, 0.09, 0.08) },
      uAtlasTexel: { value: 1 / 2048 },
      uDebug: { value: 0 },
    };

    const mat = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: DECAL_VS,
      fragmentShader: DECAL_FS,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      side: THREE.BackSide,
      blending: THREE.MultiplyBlending,
      premultipliedAlpha: true,   // MultiplyBlending == (Zero, SrcColor); alpha is unused
      toneMapped: false,
    });

    this.mesh = new THREE.Mesh(g, mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 2500;
    this.mesh.name = 'fx_decals';
    this.mesh.userData.noCollide = true;
    this.mesh.userData.fx = true;
    this.geometry = g;
  }

  /** Debug: paint the decal's local uv + coverage instead of modulating. */
  setDebug(on) {
    this.uniforms.uDebug.value = on ? 1 : 0;
    this.mesh.material.blending = on ? THREE.NormalBlending : THREE.MultiplyBlending;
    this.mesh.material.premultipliedAlpha = !on;
    this.mesh.material.needsUpdate = true;
  }

  /**
   * @param {THREE.Vector3} point   surface point
   * @param {THREE.Vector3} normal  surface normal
   * @param {object} o { tile, size, depth, tint, life, roll, normalStrength, tintStrength }
   */
  add(point, normal, o = {}) {
    const tile = o.tile ?? 0;
    const cell = this._enforceDensity(point, tile);
    if (cell.reject) { this.suppressed++; return cell.slot; }

    const slot = this._alloc();
    if (slot < 0) return -1;
    cell.list.push(slot);

    const n = _v1.copy(normal);
    if (n.lengthSq() < 1e-6) n.set(0, 1, 0);
    n.normalize();
    // orthonormal basis with +Z along the surface normal, rolled by `roll`
    const up = Math.abs(n.y) > 0.95 ? _v2.set(1, 0, 0) : _v2.set(0, 1, 0);
    _v3.crossVectors(up, n).normalize();
    _v4.crossVectors(n, _v3);
    _m3.makeBasis(_v3, _v4, n);
    _q1.setFromRotationMatrix(_m3);
    _q2.setFromAxisAngle(n, o.roll ?? 0);
    _q1.premultiply(_q2);

    const sx = o.size ?? 0.2;
    const sy = o.sizeY ?? sx;
    const depth = o.depth ?? Math.max(0.05, sx * 0.6);

    const i4 = slot * 4;
    this.iPos[i4] = point.x + n.x * depth * 0.15;
    this.iPos[i4 + 1] = point.y + n.y * depth * 0.15;
    this.iPos[i4 + 2] = point.z + n.z * depth * 0.15;
    this.iPos[i4 + 3] = 0;
    this.iQuat[i4] = _q1.x; this.iQuat[i4 + 1] = _q1.y;
    this.iQuat[i4 + 2] = _q1.z; this.iQuat[i4 + 3] = _q1.w;
    this.iScale[i4] = sx * 0.5; this.iScale[i4 + 1] = sy * 0.5;
    this.iScale[i4 + 2] = depth; this.iScale[i4 + 3] = o.tile ?? 0;
    this.iParams[i4] = 0.0001;                       // fades in
    this.iParams[i4 + 1] = o.normalStrength ?? 1.0;
    this.iParams[i4 + 2] = o.tintStrength ?? 1.0;
    this.iParams[i4 + 3] = 0;
    const t = o.tint || [0.5, 0.5, 0.5];
    this.iTint[i4] = t[0]; this.iTint[i4 + 1] = t[1];
    this.iTint[i4 + 2] = t[2]; this.iTint[i4 + 3] = 0;

    this.birth[slot] = this.time;
    this.life[slot] = o.life ?? 90;
    this.fadeOut[slot] = 0;
    this.used[slot] = 1;
    this.order.push(slot);
    this._dirty = true;
    return slot;
  }

  _key(x, y, z) {
    const c = 1 / this.cellSize;
    return `${Math.floor(x * c)},${Math.floor(y * c)},${Math.floor(z * c)}`;
  }

  /**
   * Bound the decal density around `point`.
   * @returns {{list:number[], reject:boolean, slot:number}}
   *   `reject` when this hit is a near-duplicate of one already there (its life
   *   is refreshed instead); otherwise `list` is the cell to record the new slot
   *   in, with the oldest occupant already retired if the cell was full.
   */
  _enforceDensity(point, tile) {
    const key = this._key(point.x, point.y, point.z);
    let list = this.grid.get(key);
    if (!list) { list = []; this.grid.set(key, list); }

    // prune dead / recycled-elsewhere entries
    for (let i = list.length - 1; i >= 0; i--) {
      const s = list[i];
      const j = s * 4;
      if (!this.used[s] || this._key(this.iPos[j], this.iPos[j + 1], this.iPos[j + 2]) !== key) {
        list.splice(i, 1);
      }
    }

    // A second hole 3 cm from the first adds density and no information. Refresh
    // the one that is already there rather than stacking another box on top.
    const sep2 = this.minSeparation * this.minSeparation;
    for (const s of list) {
      const j = s * 4;
      const dx = this.iPos[j] - point.x;
      const dy = this.iPos[j + 1] - point.y;
      const dz = this.iPos[j + 2] - point.z;
      if (dx * dx + dy * dy + dz * dz < sep2 && this.iScale[j + 3] === tile) {
        this.birth[s] = this.time;
        this.fadeOut[s] = 0;
        return { list, reject: true, slot: s };
      }
    }

    // cell full: retire the oldest occupant so the newest hit still registers
    while (list.length >= this.maxPerCell) {
      const s = list.shift();
      const oi = this.order.indexOf(s);
      if (oi >= 0) this.order.splice(oi, 1);
      this.used[s] = 0;
      this.iParams[s * 4] = 0;
      this._dirty = true;
    }
    return { list, reject: false, slot: -1 };
  }

  _alloc() {
    // free slots first
    for (let i = 0; i < this.capacity; i++) {
      const s = (this.head + i) % this.capacity;
      if (!this.used[s]) { this.head = (s + 1) % this.capacity; return s; }
    }
    // full: force the oldest into a fast fade and steal the one already faded out
    for (let i = 0; i < this.order.length; i++) {
      const s = this.order[i];
      if (this.iParams[s * 4] <= 0.001) {
        this.order.splice(i, 1);
        return s;
      }
    }
    const s = this.order.shift();
    return s ?? -1;
  }

  /** Keep a reserve of slots quietly fading so allocation never pops. */
  _pressure() {
    const reserve = Math.max(8, Math.floor(this.capacity * 0.06));
    const free = this.capacity - this.order.length;
    if (free >= reserve) return;
    const need = reserve - free;
    for (let i = 0; i < need && i < this.order.length; i++) {
      const s = this.order[i];
      if (this.fadeOut[s] === 0) this.fadeOut[s] = this.time + 0.45;
    }
  }

  update(dt, env) {
    this.time += dt;
    this._pressure();

    let maxSlot = 0;
    for (let i = this.order.length - 1; i >= 0; i--) {
      const s = this.order[i];
      const age = this.time - this.birth[s];
      const life = this.life[s];
      let f = Math.min(1, age / 0.09);                       // fade in
      const fo = this.fadeOut[s];
      if (fo > 0) f *= Math.max(0, (fo - this.time) / 0.45); // forced recycle fade
      else if (age > life - 2.5) f *= Math.max(0, (life - age) / 2.5);
      if (age > life || (fo > 0 && this.time > fo)) {
        this.used[s] = 0;
        this.iParams[s * 4] = 0;
        this.order.splice(i, 1);
        this._dirty = true;
        continue;
      }
      if (this.iParams[s * 4] !== f) { this.iParams[s * 4] = f; this._dirty = true; }
      if (s + 1 > maxSlot) maxSlot = s + 1;
    }
    this.count = this.order.length;
    this.geometry.instanceCount = maxSlot;

    if (this._dirty) {
      this.aPos.needsUpdate = true;
      this.aQuat.needsUpdate = true;
      this.aScale.needsUpdate = true;
      this.aParams.needsUpdate = true;
      this.aTint.needsUpdate = true;
      this._dirty = false;
    }

    const u = this.uniforms;
    u.tDepth.value = env.depthTexture;
    u.tNormal.value = env.normalTexture || env.depthTexture;
    u.uDepthMode.value = env.depthMode;
    u.uResolution.value.copy(env.resolution);
    u.uProjInv.value.copy(env.camera.projectionMatrixInverse);
    u.uViewInv.value.copy(env.camera.matrixWorld);
    u.uViewInvRot.value.setFromMatrix4(env.camera.matrixWorld);
    u.uSunDirWorld.value.copy(env.sunDirWorld);
    u.uSunColor.value.copy(env.sunColor);
    u.uAmbSky.value.copy(env.ambSky);
    u.uAmbGround.value.copy(env.ambGround);
    u.uAtlasTexel.value = env.decalAtlasTexel;
    this.mesh.visible = !this.forceHidden && !!env.depthTexture && maxSlot > 0;
  }

  reset() {
    this.order.length = 0;
    this.used.fill(0);
    this.iParams.fill(0);
    this.fadeOut.fill(0);
    this.grid.clear();
    this.suppressed = 0;
    this.head = 0;
    this.count = 0;
    this.geometry.instanceCount = 0;
    this._dirty = true;
  }

  /** Worst-case decal overlap for a point on a surface, for the FX budget report. */
  densityStats() {
    let maxCell = 0, cells = 0, live = 0;
    for (const [, list] of this.grid) {
      let n = 0;
      for (const s of list) if (this.used[s]) n++;
      if (n) { cells++; live += n; if (n > maxCell) maxCell = n; }
    }
    return {
      live: this.count, cells, maxPerCell: maxCell, cap: this.maxPerCell,
      meanPerCell: cells ? +(live / cells).toFixed(2) : 0,
      suppressed: this.suppressed, cellSize: this.cellSize,
    };
  }

  dispose() {
    this.geometry.dispose();
    this.mesh.material.dispose();
  }
}

const _v1 = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _v3 = new THREE.Vector3();
const _v4 = new THREE.Vector3();
const _m3 = new THREE.Matrix4();
const _q1 = new THREE.Quaternion();
const _q2 = new THREE.Quaternion();
